/**
 * Die Entwickler im Dreier-Chat — EIN Besitzer der Liste. (16.10.2026, Zimmermann P1)
 *
 * Martin, 16.10. 18:22: „Ja zu allem“ — neben dem „Schmied“ (Entwickler-Sitzung auf diesem Server, shared/schmied.ts) kommt der
 * „Zimmermann“ in den Chat: eine eigene, schlanke Sitzung für Panel, Newsletter-Versand und Faktura.
 *
 * Was hier steht, rechnen Server (Tür, Grenzen, Faden, Fremd-Marke), Chat-Blase und Einstellungen gleich:
 *   - `id`          — steht in `metadata.entwickler` jeder Beitragszeile; eine Zeile OHNE id ist vom Schmied (Altzeilen vor P1), eine mit
 *                     unbekannter id von niemandem Bekanntem — nie vom Schmied (`entwicklerIdAus`, shared/entwickler-im-chat.ts);
 *   - `name`        — wie Martin und Vespera ihn nennen;
 *   - `blasenName`  — der sichtbare Name über der Blase (19.10.2026, Martin: der Name des Absenders — beim Schmied „Schmied“, nicht mehr „Entwickler“);
 *   - `rufname`     — alle Schreibweisen der Spracherkennung als Muster (ohne Gruppe drumherum);
 *   - `stimmeVorgabe`, `tempo` — Soniox-Stimme und Sprechtempo;
 *   - `farbe`       — der Präfix seiner CSS-Variablen (frontend/style.css), Klasse an der Blase: `e-<id>`;
 *   - `fremdQuelle` — die Quelle, mit der sein unbeantworteter Beitrag Martins Zug fremd macht (server/dreier-modus.ts);
 *   - `lockerbar`   — nur ein Entwickler mit `true` darf je von der Werkzeug-Sperre ausgenommen werden (Martin 16.10., Entscheidung 2:
 *                     der Zimmermann NIE — seine Beiträge sperren Vesperas Werkzeuge im Zug wie Fremdtext);
 *   - `stimmeFeld`  — der Kontowert seiner gewählten Stimme (Auswahl aus SCHMIED_STIMMEN, shared/schmied.ts);
 *   - `kontoFeld`, `notschalter` — sein eigener Kontoschalter (unter „Entwickler im Chat“) und der Umgebungs-Schalter; der Schmied
 *                     hat beides nicht (für ihn gilt allein „Entwickler im Chat“ und `CONNECT`).
 *
 * ⚠️ Ein Beitrag — von wem auch immer — ist DATEN: nie Martins Ja, nie ein Auftrag, er beginnt nie einen Zug.
 */

/** Die Kennungen. Neue Entwickler kommen NUR hier dazu. (17.10.2026: der „Hüter“ auf S7 — Sicherungen.) */
export type EntwicklerId = "schmied" | "zimmermann" | "hueter";

/**
 * Die ZUSATZ-Entwickler (17.10.2026, Martin 19:46): alle außer dem Schmied — eigener Rechner, eigenes Token, eigener Kontoschalter
 * und Notschalter, eigener Adressat („An: …“, Anrede am Anfang), eigene Statuszeile „<Name> arbeitet / hat deine Nachricht“.
 * Was für den Zimmermann gebaut wurde, gilt für jeden hier gleich (server/adressat-jev.ts `zusatzWeg`, server/dreier-routen.ts).
 */
export type ZusatzId = Exclude<EntwicklerId, "schmied">;
export const ZUSATZ_IDS: readonly ZusatzId[] = ["zimmermann", "hueter"];

/** Ist das ein Zusatz-Entwickler (nicht der Schmied)? Rein. */
export function istZusatzId(wert: unknown): wert is ZusatzId {
  return typeof wert === "string" && (ZUSATZ_IDS as readonly string[]).includes(wert);
}

export interface Entwickler {
  id: EntwicklerId;
  name: string;
  blasenName: string;
  /** Regex-Teil (ohne Anker, Groß/klein egal, Wortgrenzen setzt der Aufrufer mit `\p{L}`). */
  rufname: string;
  stimmeVorgabe: string;
  tempo: number;
  farbe: string;
  fremdQuelle: string;
  lockerbar: boolean;
  kontoFeld: string | null;
  notschalter: string | null;
  /** Der Kontowert seiner gewählten Stimme (16.10.2026, Seite „Entwickler-Team“; Liste und Prüfung: shared/schmied.ts). */
  stimmeFeld: string;
  /** Der Kontowert seines Sprechtempos (17.10.2026; Grenzen und Prüfung: shared/schmied.ts `entwicklerTempoAus`). `null`/fehlt = `tempo`. */
  tempoFeld: string;
  /** Der Kontowert „Ausführlichkeit“ (17.10.2026; shared/schmied.ts `ausfuehrlichkeitAus`). Nur eine Anzeige für den Mithör-Kopf. */
  ausfuehrlichkeitFeld: string;
  /**
   * Sein Rechner als Anrede-Muster (17.10.2026: „Server 6“ = Zimmermann, „Server 7“ = Hüter) — nur für die ANREDE am Anfang einer
   * Nachricht (shared/schmied.ts `anEntwicklerGerichtet`). Der Schmied hat keinen (Server 8 ist kein Name).
   */
  rechner: string | null;
  /** Sein Rechner, wie Martin ihn nennt („Server 6“) — nur Anzeige (Einstellungen). */
  rechnerName: string | null;
  /** Wofür er da ist — ein kurzer Satz für Richter/Jev und die Einstellungen. */
  gebiet: string;
  /**
   * Hört er den GANZEN Chat (auch Martin ↔ Vespera, Privates)? Seit 19.10.2026 (Martin 07:28) NUR der Schmied (`true`), und auch er nur
   * im Chat des Kontos 1 (server/dreier-routen.ts `schmiedHoertAllesAn`). Zimmermann und Hüter (`false`) sehen die Beiträge der Entwickler
   * und Martins Nachrichten, die ausdrücklich AN SIE gingen (`an` = ihre Kennung); alles zwischen Martin und Vespera ist für sie PRIVAT
   * (`siehtZeile`). Das Feld bleibt als Wächter.
   */
  hoertAlles: boolean;
  /**
   * Darf Martin ihm AUF ZURUF (befristetes Fenster, server/dreier-privat-fenster.ts) auch das Private mitzeigen? Nur der Schmied
   * (Martin 18.10.2026: „damit ich ihn bei nicht stringenten Themen auf einen Bug ansetzen kann“). Zimmermann und Hüter NIE.
   */
  privatAufZuruf: boolean;
}

/** Wer eine Zeile ohne Kennung geschrieben hat (alle Beiträge vor dem 16.10.2026). */
export const ENTWICKLER_VORGABE: EntwicklerId = "schmied";

/** Der Kontoschalter „Zimmermann im Chat“ (nur Konto 1, Vorgabe AUS). */
export const ZIMMERMANN_IM_CHAT_FELD = "zimmermannImChat";

/** Die gewählte Stimme des Zimmermanns (nur Konto 1, `null` = Vorgabe; nie auf Zuruf — shared/einstellung-aendern.ts). */
export const ZIMMERMANN_STIMME_FELD = "zimmermannStimme";

/** Das Sprechtempo und die Ausführlichkeit je Entwickler (nur Konto 1, `null` = Vorgabe; nie auf Zuruf — shared/einstellung-aendern.ts). */
export const SCHMIED_TEMPO_FELD = "entwicklerTempo";
export const ZIMMERMANN_TEMPO_FELD = "zimmermannTempo";
export const SCHMIED_AUSFUEHRLICHKEIT_FELD = "entwicklerAusfuehrlichkeit";
export const ZIMMERMANN_AUSFUEHRLICHKEIT_FELD = "zimmermannAusfuehrlichkeit";

/** Der Notschalter in der Umgebung: nur `ENTWICKLER_ZIMMERMANN=1` lässt ihn herein (Vorgabe aus, bis Martin einschaltet). */
export const ZIMMERMANN_NOTSCHALTER = "ENTWICKLER_ZIMMERMANN";

/** Der Hüter (17.10.2026): dieselben vier Kontowerte und ein eigener Notschalter `ENTWICKLER_HUETER=1` (Vorgabe aus). */
export const HUETER_IM_CHAT_FELD = "hueterImChat";
export const HUETER_STIMME_FELD = "hueterStimme";
export const HUETER_TEMPO_FELD = "hueterTempo";
export const HUETER_AUSFUEHRLICHKEIT_FELD = "hueterAusfuehrlichkeit";
export const HUETER_NOTSCHALTER = "ENTWICKLER_HUETER";

export const ENTWICKLER: readonly Entwickler[] = [
  {
    id: "schmied",
    name: "Schmied",
    blasenName: "Schmied",
    // Dieselben Schreibweisen wie shared/schmied.ts (SCHMIED_RUFNAME) — der Schmied bleibt unverändert.
    rufname: "(?:schmied|schmidt|schmiet|schmid)",
    stimmeVorgabe: "soniox:Jonas",
    tempo: 1.08,
    farbe: "--entwickler",
    fremdQuelle: "entwickler-beitrag",
    lockerbar: true,
    kontoFeld: null,
    notschalter: null,
    stimmeFeld: "entwicklerStimme",
    tempoFeld: SCHMIED_TEMPO_FELD,
    ausfuehrlichkeitFeld: SCHMIED_AUSFUEHRLICHKEIT_FELD,
    rechner: null,
    rechnerName: null,
    gebiet: "Code, Fehler, Tickets, Bau und Ausliefern der App",
    // 19.10.2026 (Martin 07:28, Ja-Knopf): der Schmied liest den GANZEN Chat mit — nur im Chat des Kontos 1 und nur über
    // server/dreier-routen.ts `schmiedHoertAllesAn` (Notschalter SCHMIED_HOERT_ALLES=0). Zimmermann und Hüter bleiben false.
    hoertAlles: true,
    privatAufZuruf: true,
  },
  {
    id: "zimmermann",
    name: "Zimmermann",
    blasenName: "Zimmermann",
    // ⚠️ Das alleinstehende „Zimmer“ ist NIE eine Anrede („Geh ins Zimmer“) — nur die ganzen Namensformen. Längste zuerst.
    rufname: "(?:zimmermanns|zimmermann|zimmerman|zimmer-mann|zimmer\\s+mann|timmermann)",
    stimmeVorgabe: "soniox:Florian",
    tempo: 1.05,
    farbe: "--entwickler-zimmermann",
    fremdQuelle: "entwickler-beitrag:zimmermann",
    lockerbar: false,
    kontoFeld: ZIMMERMANN_IM_CHAT_FELD,
    notschalter: ZIMMERMANN_NOTSCHALTER,
    stimmeFeld: ZIMMERMANN_STIMME_FELD,
    tempoFeld: ZIMMERMANN_TEMPO_FELD,
    ausfuehrlichkeitFeld: ZIMMERMANN_AUSFUEHRLICHKEIT_FELD,
    // „Server 6“, „Server sechs“, „Server-6“, „S6“ — „Server 8“ nie (Martin 17.10.2026).
    rechner: "(?:server[\\s-]?(?:6|sechs)|s6)",
    rechnerName: "Server 6",
    gebiet: "Panel, Newsletter, Faktura",
    // 18.10.2026 (Martin 08:46): wie der Hüter — vorher true.
    hoertAlles: false,
    privatAufZuruf: false,
  },
  {
    // 17.10.2026 (Martin 19:46): Server 7, der Sicherungs-Server. Er bewahrt Sicherungen, prüft Spiegel und Wiederherstellbarkeit nur
    // STREAMEND (nie entpacken) und warnt — er entpackt, löscht, gleicht ab und spielt nie zurück ohne Martins Ja in seiner Sitzung
    // (scripts/entwickler/CLAUDE-hueter.md).
    id: "hueter",
    name: "Hüter",
    blasenName: "Hüter",
    // ⚠️ Nur der Name selbst (mit Wortgrenze ohne `\b`): „hüten“, „Hüte“, „Torhüter“, „Hüterin“ treffen nie. Die Spracherkennung schreibt
    // ihn auch „Hueter“, „Hüther“ oder „Hütter“. Längste zuerst.
    rufname: "(?:hüters|hüter|hueters|hueter|hüther|hütter)",
    // Eine ruhige weibliche Muttersprach-Stimme — hörbar anders als Schmied (Jonas) und Zimmermann (Florian).
    stimmeVorgabe: "soniox:Annika",
    tempo: 1.0,
    farbe: "--entwickler-hueter",
    fremdQuelle: "entwickler-beitrag:hueter",
    lockerbar: false,
    kontoFeld: HUETER_IM_CHAT_FELD,
    notschalter: HUETER_NOTSCHALTER,
    stimmeFeld: HUETER_STIMME_FELD,
    tempoFeld: HUETER_TEMPO_FELD,
    ausfuehrlichkeitFeld: HUETER_AUSFUEHRLICHKEIT_FELD,
    // „Server 7“, „Server sieben“, „Server-7“, „S7“.
    rechner: "(?:server[\\s-]?(?:7|sieben)|s7)",
    rechnerName: "Server 7",
    gebiet: "Sicherungen bewahren, Spiegel und Wiederherstellbarkeit nur lesend prüfen, warnen",
    hoertAlles: false,
    privatAufZuruf: false,
  },
];

const NACH_ID: ReadonlyMap<string, Entwickler> = new Map(ENTWICKLER.map((e) => [e.id, e]));

/** Ist das eine bekannte Kennung? Rein. */
export function istEntwicklerId(wert: unknown): wert is EntwicklerId {
  return typeof wert === "string" && NACH_ID.has(wert);
}

/**
 * Der Eintrag zu einer Kennung. ⚠️ NUR für Anzeige und Werte (Name, Stimme, Farbe): eine unbekannte Kennung bekommt den Eintrag des
 * Schmieds, damit eine Blase einen Namen hat. WER einen Beitrag schrieb und ob er wie der Schmied behandelt wird, sagt allein
 * `entwicklerIdAus` (shared/entwickler-im-chat.ts): fehlt = Schmied, unbekannt = null = NIE Schmied. Rein.
 */
export function entwickler(id: unknown): Entwickler {
  return NACH_ID.get(istEntwicklerId(id) ? id : ENTWICKLER_VORGABE) ?? ENTWICKLER[0]!;
}

/** Alle Quellen, mit denen ein Beitrag einen Zug fremd macht (server/injektions-schutz.ts). */
export const ENTWICKLER_FREMD_QUELLEN: readonly string[] = ENTWICKLER.map((e) => e.fremdQuelle);

/**
 * Darf dieser Entwickler im Chat sein — sein Notschalter in der Umgebung? Der Schmied hat keinen (true). Für die anderen gilt nur ein
 * ausdrückliches `1`; fehlt die Variable oder steht etwas anderes darin, sind sie draußen. Rein.
 */
export function notschalterErlaubt(id: EntwicklerId, env: Readonly<Record<string, string | undefined>>): boolean {
  const n = entwickler(id).notschalter;
  return n === null || env[n]?.trim() === "1";
}
